import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import { useMediaQuery } from 'react-responsive';
import PageLabel from 'src/components/PageLable';
import { useContext, useMemo } from 'react';
import StepperContext from 'src/store/stepper-context';
import Steps from 'src/components/steps/steps';
import { uniqueId } from 'lodash';
import AuthContext from 'src/store/auth-context';
import { useHistory } from 'react-router-dom';

/**
 * WebStepper is a component that displays a stepper UI for navigating through a multi-step process.
 * Each step in the webstepper UI is a clickable button that will naviage to a specific step in the multi-step process.
 * The component also adapts its layout based on the screen width, displaying the step labels only on desktop screens
 *
 * @param {Object} props - The props for the component.
 *
 * @returns {ReactElement} - A React element that renders the stepper UI and the current step component.
 */
const WebStepper = () => {
  const history = useHistory();

  const { stepNo, formikRef, setJumpToStep, nextStep } = useContext(StepperContext);
  const { account, backdoor } = useContext(AuthContext);

  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });

  // Use useMemo to memoize the rendered steps
  const formSteps = useMemo(
    () =>
      Steps.map(({ Component, ...props }, idx) => {
        return (
          <Component
            isFirstStep={idx === 0}
            isLastStep={idx === Steps.length - 1}
            key={uniqueId()}
            {...props}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Steps]
  );

  const handleStepChange = (stepIndex) => {
    if (stepIndex > stepNo) {
      setJumpToStep(stepIndex);
      submitFormOrGoToNextStep(stepIndex - stepNo);
    } else {
      nextStep(stepIndex - stepNo);
    }
  };

  /**
   * Handles form submission or progression to the next step.
   *
   * - For regular users (not logged in via the admin portal):
   *   The form is submitted, subject to its validation rules.
   *
   * - For backdoor users (i.e., admins accessing via the portal):
   *   The form is NOT submitted to prevent unintended changes.
   *   Instead, the user proceeds directly to the next step, bypassing any validation.
   */
  const submitFormOrGoToNextStep = (nextStepIndex) => {
    if (!backdoor) {
      formikRef.current.submitForm();
    } else {
      nextStep(nextStepIndex);
    }
  };

  //check if the account is already signed and there is no backdoor access
  if (account.signed === 'Y' && !backdoor) {
    // If either condition is met, redirect the user to the "/complete" page
    history.push('/complete');
  }

  return (
    <>
      {!isDesktop && formSteps[stepNo]?.props?.label && (
        <PageLabel label={formSteps[stepNo]?.props?.label} />
      )}

      <Stepper nonLinear activeStep={stepNo} sx={{ my: '1rem', mx: '0.75rem' }}>
        {formSteps.map(({ props }, stepIndex) => {
          return (
            <Step key={uniqueId()}>
              <StepButton
                name={props.label}
                color="blue"
                onClick={() => handleStepChange(stepIndex)}
                disableRipple
              >
                {isDesktop ? props.label : ''}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </>
  );
};

export default WebStepper;
